import { useLanguage } from '@/context/LanguageContext';
import { Link } from 'react-router-dom';
import { TrendingUp, Shield, Globe, Landmark, Droplets, Cpu, ArrowRight, CheckCircle2 } from 'lucide-react';

const INVEST_IMAGE = 'https://images.unsplash.com/photo-1761999257749-6806ad6b87e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGludmVzdG1lbnQlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc1NTEyMzMwfDA&ixlib=rb-4.1.0&q=85';

export default function InvestInAngola() {
  const { t } = useLanguage();

  const sectors = [
    { icon: Droplets, title: t('invest.sector_oil'), desc: t('invest.sector_oil_desc') },
    { icon: Landmark, title: t('invest.sector_mining'), desc: t('invest.sector_mining_desc') },
    { icon: Globe, title: t('invest.sector_agri'), desc: t('invest.sector_agri_desc') },
    { icon: Cpu, title: t('invest.sector_tech'), desc: t('invest.sector_tech_desc') },
    { icon: TrendingUp, title: t('invest.sector_infra'), desc: t('invest.sector_infra_desc') },
    { icon: Shield, title: t('invest.sector_tourism'), desc: t('invest.sector_tourism_desc') },
  ];

  const advantages = [
    t('invest.adv_1'), t('invest.adv_2'), t('invest.adv_3'),
    t('invest.adv_4'), t('invest.adv_5'), t('invest.adv_6'),
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="invest-page">
      {/* Hero */}
      <div className="relative h-[340px] md:h-[400px] overflow-hidden">
        <img src={INVEST_IMAGE} alt="Investir em Angola" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FFD700] mb-3">{t('invest.overline')}</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight font-serif">{t('invest.title')}</h1>
          <p className="text-base text-white/80 max-w-2xl mt-4">{t('invest.subtitle')}</p>
        </div>
      </div>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight font-serif mb-6">{t('invest.why_title')}</h2>
            <p className="text-gray-600 leading-relaxed">{t('invest.why_desc')}</p>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight font-serif">{t('invest.advantages_title')}</h2>
            <div className="w-16 h-1 bg-[#CC0000] mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {advantages.map((adv, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-white border border-gray-100 rounded-md">
                <CheckCircle2 size={18} className="text-[#CC0000] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">{adv}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Sectors */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight font-serif">{t('invest.sectors_title')}</h2>
            <div className="w-16 h-1 bg-[#CC0000] mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sectors.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="p-6 border border-gray-100 rounded-md hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-4">
                  <Icon size={20} className="text-[#CC0000]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white font-serif mb-4">{t('invest.cta_title')}</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">{t('invest.cta_desc')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/oportunidades" className="inline-flex items-center gap-2 px-6 py-3 bg-[#CC0000] text-white font-medium rounded-sm hover:bg-[#A30000] transition-colors" data-testid="invest-opportunities-btn">
              {t('invest.cta_opportunities')} <ArrowRight size={16} />
            </Link>
            <Link to="/contactos" className="inline-flex items-center gap-2 px-6 py-3 border-2 border-white/30 text-white font-medium rounded-sm hover:bg-white/10 transition-colors" data-testid="invest-contact-btn">
              {t('invest.cta_contact')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
